import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import storage from "@/services/storage";
import { toastMsg } from "@/services/variables";
import { getCookie, jsonParser } from "@/services/generic.methods";
import { globalLogout } from "@/services/generic-logout-method";
import { useDispatch, useSelector } from "react-redux";
import {
  getLoggedinUser,
  setProfileToState,
} from "@/store/slices/auth/authSlice";
import { selectRoles, setRolesToState } from "@/store/slices/roles/rolesSlice";
import { setFeaturesToState, selectFeaturesLoaded } from "@/store/slices/features/featuresSlice";
import RoleEngine from "@/services/role-engine";
import { useRouter } from "next/router";
import metaRoutes from "./meta-routes";
import UilCircularLoader from "@/components/circular-loader-uil";
import { usePathname } from "next/navigation";
import { getHomePath } from "@/routes/home-path";

const RouteGuard = (props) => {
  const { children } = props;
  const [isAuth, setAuth] = useState(false);
  const [isAllowed, setIsAllowed] = useState(true);
  const profile = useSelector(getLoggedinUser);
  const roleState = useSelector(selectRoles);
  const permissions = roleState?.roles;
  const featuresLoaded = useSelector(selectFeaturesLoaded);
  const dispatch = useDispatch();
  const router = useRouter();
  const isAuthenticated = !!getCookie("CRM-Auth");
  const isCoreAuthToken = !!getCookie("Auth");
  const isLocalhost = process.env.NODE_ENV === "development";
  const pathname = usePathname();

  const getStoredProfile = () => {
    const profileStr = storage.getItem("Profile");
    return profileStr ? JSON.parse(profileStr) : null;
  };

  const logoutHandler = () => {
    setAuth(false);
    let shouldRedirect = true;
    globalLogout(shouldRedirect);
  };

  const sessionExpireHandler = () => {
    setAuth(false);
    toast.error(toastMsg.errors.inValidAuthToken);
    let shouldRedirect = true;
    globalLogout(shouldRedirect);
  };

  useEffect(() => {
    const user = getStoredProfile();
    const redirectionData = jsonParser(
      storage.getItem("report_redirect") || "",
    );
    if (router?.query?.reportId) {
      if (isAuthenticated) {
        location.replace(`${pathname}/${router?.query?.reportId}`);
      } else {
        storage.setItem(
          "report_redirect",
          `${pathname}/${router?.query?.reportId}`,
        );
      }
    } else if (redirectionData) {
      router.push(redirectionData);
    }

    //set isAuth to false when logout, and show loading for few seconds
    if (
      !isAuthenticated ||
      // (!isCoreAuthToken && !isLocalhost) ||
      (isAuthenticated && !user)
    ) {
      logoutHandler();
    }

    // only call when session expires
    if (!isAuthenticated && user) {
      sessionExpireHandler();
    }

    if (isAuthenticated && user) {
      setAuth(true);
      if (!profile) {
        dispatch(setProfileToState());
      }
      if (!featuresLoaded) {
        dispatch(setFeaturesToState(user));
      }
      if (!permissions || (!Object.keys(permissions).length && user?.RoleType)) {
        let tempRoles = RoleEngine(user?.RoleType, user);
        dispatch(setRolesToState(tempRoles));
        checkForRoutePermission(tempRoles);
      } else if (!user?.RoleType) {
        router.push("/permission-denied");
      }
    }
  }, [isAuthenticated]);

  // Re-check route permission on navigation (not only on first role hydration)
  useEffect(() => {
    const user = getStoredProfile();
    if (
      isAuthenticated &&
      user?.RoleType &&
      permissions &&
      Object.keys(permissions).length
    ) {
      checkForRoutePermission(permissions);
    }
  }, [pathname, permissions, isAuthenticated]);

  const checkForRoutePermission = (permissions) => {
    let path = router.pathname;
    // stop screen on loading untill this function runs
    setIsAllowed(false);
    let currentRoute = metaRoutes.filter((r) => path === r.path);
    let allowed = true;

    if (
      currentRoute.length &&
      currentRoute[0]?.name &&
      currentRoute[0].secondKey
    ) {
      let key = currentRoute[0].name; // Allows dot notation like "analytics.reports"
      let secondKey = currentRoute[0].secondKey;

      // Ensure we're checking specifically for 'view' permission when parsing route access
      try {
        // Resolve nested key if it exists (e.g., permissions['analytics']['reports'])
        const keys = key.split(".");
        let resolvedNode = permissions;

        for (const k of keys) {
          if (!resolvedNode[k]) {
            resolvedNode = null;
            break;
          }
          resolvedNode = resolvedNode[k];
        }

        if (!resolvedNode) {
          allowed = false;
        } else if (secondKey === "view" || secondKey === "edit") {
          if (!resolvedNode[secondKey]) {
            allowed = false;
          }
        } else if (resolvedNode[secondKey]) {
          if (!resolvedNode[secondKey].view) {
            allowed = false;
          }
        } else if (!resolvedNode.view) {
          allowed = false;
        }
      } catch (err) {
        allowed = false;
      }
    }

    if (!allowed) {
      const homePath = getHomePath(permissions);
      if (homePath !== "/crm") {
        if (path !== homePath) {
          router.push(homePath);
          setIsAllowed(false);
          return;
        }
      } else {
        router.push("/permission-denied");
        setIsAllowed(false);
        return;
      }
    }

    setIsAllowed(true);
  };

  return isAuth && isAllowed ? (
    children
  ) : (
    <div
      className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "calc(100dvh - 220px)" }}
    >
      <UilCircularLoader />
    </div>
  );
};

export default RouteGuard;
